import * as XLSX from 'xlsx';

/**
 * Parse number safely
 */
const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(value);
  return isNaN(num) ? null : num;
};

/**
 * Parse integer safely
 */
const parseIntSafe = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = parseInt(value);
  return isNaN(num) ? null : num;
};

/**
 * Parse Excel or CSV file and convert to processor objects
 * @param {File} file - The Excel or CSV file to parse
 * @returns {Promise<Array>} Array of processor objects in API format
 */
export const parseProcessorExcelFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });
        // Transform Excel data to processor API format
        const processors = jsonData.map((row, index) => {
          // Map Excel columns to database fields based on stored procedures
          // Excel has merged cells creating __EMPTY columns
          
          // Business Profile columns (sp_InsertBusinessProfile parameters)
          const businessName = row['1. Business Profile'] || row['Name of Business'];
          const address = row['__EMPTY'];
          const contact = row['__EMPTY_1'];
          const yearsInOperation = row['__EMPTY_2'];
          const status = row['__EMPTY_3'];
          const businessType = row['__EMPTY_4'];
          const ownerName = row['__EMPTY_5'];
          const ownerAddress = row['__EMPTY_6'];
          const initialInvestment = row['__EMPTY_7'];
          const annualIncome = row['__EMPTY_8'];
          const numberOfEmployees = row['__EMPTY_9'];
          const type = row['__EMPTY_10']; // TYPE field
          
          // Processing data (sp_InsertProcessing parameters in order):
          // p_cacaoQty, p_processingFrequency, p_preferredVariety, 
          // p_processedVariety, p_sourceCacao, p_producersToProcessors, p_localTradersToProcessors,
          // p_deliveryFrequency, p_deliveryQuantity, p_cacaoForm
          const cacaoQty = row['2']; // Quantity_of_Cacao_Beans_per_Processing_Cycle_kg
          const processingFrequency = row['3']; // Frequency_of_Processing
          const preferredVariety = row['4. Processing Practices']; // 4_1_Preferred_Variety_of_Cacao
          const processedVariety = row['__EMPTY_11']; // 4_2_Variety_of_Cacao_Processed
          const sourceCacao = row['__EMPTY_12']; // 4_3_Source_of_Cacao
          const producersToProcessors = row['__EMPTY_13']; // 4_4_1_Producers_to_Processors
          const localTradersToProcessors = row['__EMPTY_14']; // 4_4_2_Local_Traders_to_Processors
          const deliveryFrequency = row['__EMPTY_15']; // 4_5_Frequency_of_Delivery_in_a_month
          const deliveryQuantity = row['__EMPTY_16']; // 4_6_Quantity_Kilograms_of_Cacao_Delivered
          const cacaoForm = row['__EMPTY_17']; // 4_7_Form_Type_of_Cacao_Bought
          
          // There's a gap column at __EMPTY_18 in Excel
          // Post Harvest data (sp_InsertPostHarvest parameters in order):
          // p_podBreaking, p_beanExtraction, p_fermentation, p_containment, p_days, p_tempMonitoring,
          // p_preferredTemp, p_drying, p_dryingType, p_moistureContent, p_mcMethod, p_beanSorting,
          // p_beanSize, p_beanGrading, p_fermentationLevel, p_aroma, p_storageMaterials, p_storageCost
          const podBreaking = row['__EMPTY_19']; // 4_8_Pod_Breaking
          const beanExtraction = row['__EMPTY_20']; // 4_9_Bean_Extraction
          const fermentation = row['__EMPTY_21']; // 4_10_Fermentation
          const containment = row['__EMPTY_22']; // Containment_Materials
          const days = row['__EMPTY_23']; // Number_of_Days
          const tempMonitoring = row['__EMPTY_24']; // Temperature_Monitoring
          const preferredTemp = row['__EMPTY_25']; // Preferred_Fermentation_Temperature
          const drying = row['__EMPTY_26']; // 4_11_Drying_ReDrying
          const dryingType = row['__EMPTY_27']; // Type_Drying
          const moistureContent = row['__EMPTY_28']; // 4_11_1_Preferred_Moisture_Content
          const mcMethod = row['__EMPTY_29']; // Method_of_MC_Determination
          const beanSorting = row['__EMPTY_30']; // 4_12_Bean_Sorting
          const beanSize = row['__EMPTY_31']; // Usual_Bean_Size
          const beanGrading = row['__EMPTY_32']; // 4_13_Bean_Grading
          const fermentationLevel = row['__EMPTY_33']; // Level_of_Fermentation_percent
          const aroma = row['__EMPTY_34']; // Raw_Bean_Aroma
          const storageMaterials = row['__EMPTY_35']; // 4_14_Materials_used_for_Dried_Bean_Storage
          const storageCost = row['__EMPTY_36']; // Estimated_Cost_of_Materials
          
          // Techniques data (sp_InsertTechniques parameters in order):
          // p_roasting, p_roastingMethod, p_roastingTime, p_roastTempMonitoring, p_preferredRoastTemp,
          // p_cracking, p_crackingType, p_winnowing, p_winnowingType, p_grinding, p_grindingType,
          // p_conching, p_microns, p_tempering, p_temperingType, p_temperingTemp, p_moulding,
          // p_mouldingType, p_storageProcessed, p_storageTemp, p_storageRH
          const roasting = row['__EMPTY_37']; // 4_15_Roasting
          const roastingMethod = row['__EMPTY_38']; // Roasting_Method
          const roastingTime = row['__EMPTY_39']; // Average_Time_Spent
          const roastTempMonitoring = row['__EMPTY_40']; // Roasting_Temperature_Monitoring
          const preferredRoastTemp = row['__EMPTY_41']; // Preferred_Roasting_Temperature
          const cracking = row['__EMPTY_42']; // 4_16_Cracking
          const crackingType = row['__EMPTY_43']; // Cracking_Type
          const winnowing = row['__EMPTY_44']; // 4_17_Winnowing
          const winnowingType = row['__EMPTY_45']; // Winnowing_Type
          const grinding = row['__EMPTY_46']; // 4_18_Grinding
          const grindingType = row['__EMPTY_47']; // Grinding_Type
          const conching = row['__EMPTY_48']; // 4_19_Conching
          const microns = row['__EMPTY_49']; // Preferred_Microns_Size
          const tempering = row['__EMPTY_50']; // 4_20_Tempering
          const temperingType = row['__EMPTY_51']; // Tempering_Type
          const temperingTemp = row['__EMPTY_52']; // Preferred_Tempering_Temperature
          const moulding = row['__EMPTY_53']; // 4_21_Moulding
          const mouldingType = row['__EMPTY_54']; // Moulding_Type
          const storageProcessed = row['__EMPTY_55']; // 4_22_Storage_of_Processed_Product
          const storageTemp = row['__EMPTY_56']; // Preferred_Storage_Temperature
          const storageRH = row['__EMPTY_57']; // Preferred_Relative_Humidity_RH
          
          // Marketing data (sp_InsertMarketing parameters in order):
          // p_marketing, p_productType, p_marketingType, p_market, p_marketplace, p_localMarket, p_international
          const marketing = row['__EMPTY_58']; // 5_Marketing
          const productType = row['__EMPTY_59']; // 5_1_Type_of_Product_Sold
          const marketingType = row['__EMPTY_60']; // 5_2_Type_of_Marketing
          const market = row['__EMPTY_61']; // 5_3_Market
          const marketplace = row['__EMPTY_62']; // Marketplace
          const localMarket = row['__EMPTY_63']; // Local_Market
          const international = row['__EMPTY_64']; // International
          
          // Insights data (sp_InsertInsights parameters in order):
          // p_interest, p_challenges, p_supports, p_plans
          const interest = row['__EMPTY_65']; // What_interest_you_on_cacao_processing_tableya_chocolate
          const challenges = row['__EMPTY_66']; // challenges_encountered_in_processing_tableya_chocolate
          const supports = row['__EMPTY_67']; // What_supports_you_need_to_improve_business
          const plans = row['__EMPTY_68']; // What_are_your_future_plans
          
          // GPS Coordinates
          const latitude = row['GPS Coordinates'];
          const longitude = row['__EMPTY_69'];
          
          // Build the API format matching AddProcessorForm structure
          const processorData = {
            // Business Information at root level
            name: businessName && businessName !== 'N/A' ? businessName : null,
            owner: ownerName,
            address: address,
            contact: contact,
            typeBusiness: businessType,
            status: status || 'Inherited',
            years: parseIntSafe(yearsInOperation),
            investment: parseNumber(initialInvestment),
            income: parseNumber(annualIncome),
            employees: parseIntSafe(numberOfEmployees),
            type: type,
            lat: parseNumber(latitude),
            long: parseNumber(longitude),
            ownerAddress: ownerAddress,
            
            // Processing Data (nested) - matches sp_InsertProcessing
            processingData: {
              cacaoQty: parseNumber(cacaoQty),
              processingFrequency: processingFrequency,
              preferredVariety: preferredVariety,
              processedVariety: processedVariety,
              sourceCacao: sourceCacao,
              producersToProcessors: producersToProcessors,
              localTradersToProcessors: localTradersToProcessors,
              deliveryFrequency: deliveryFrequency,
              deliveryQuantity: parseNumber(deliveryQuantity),
              cacaoForm: cacaoForm
            },
            
            // Post Harvest Data (nested) - matches sp_InsertPostHarvest
            postHarvestData: {
              podBreaking: podBreaking,
              beanExtraction: beanExtraction,
              fermentation: fermentation,
              containment: containment,
              days: parseIntSafe(days),
              tempMonitoring: tempMonitoring,
              preferredTemp: parseNumber(preferredTemp),
              drying: drying,
              dryingType: dryingType,
              moistureContent: parseNumber(moistureContent),
              mcMethod: mcMethod,
              beanSorting: beanSorting,
              beanSize: beanSize,
              beanGrading: beanGrading,
              fermentationLevel: parseNumber(fermentationLevel),
              aroma: aroma,
              storageMaterials: storageMaterials,
              storageCost: parseNumber(storageCost)
            },
            
            // Techniques Data (nested) - matches sp_InsertTechniques
            techniquesData: {
              roasting: roasting,
              roastingMethod: roastingMethod,
              roastingTime: roastingTime,
              roastTempMonitoring: roastTempMonitoring,
              preferredRoastTemp: parseNumber(preferredRoastTemp),
              cracking: cracking,
              crackingType: crackingType,
              winnowing: winnowing,
              winnowingType: winnowingType,
              grinding: grinding,
              grindingType: grindingType,
              conching: conching,
              microns: parseNumber(microns),
              tempering: tempering,
              temperingType: temperingType,
              temperingTemp: parseNumber(temperingTemp),
              moulding: moulding,
              mouldingType: mouldingType,
              storageProcessed: storageProcessed,
              storageTemp: parseNumber(storageTemp),
              storageRH: parseNumber(storageRH)
            },
            
            // Marketing Data (nested) - matches sp_InsertMarketing
            marketingData: {
              marketing: marketing,
              productType: productType,
              marketingType: marketingType,
              market: market,
              marketplace: marketplace,
              localMarket: localMarket,
              international: international
            },
            
            // Insights Data (nested) - matches sp_InsertInsights
            insightsData: {
              interest: interest,
              challenges: challenges,
              supports: supports,
              plans: plans
            }
          };
          return processorData;
        });
        
        resolve(processors);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Export processors data to Excel file
 * @param {Array} processors - Array of processor objects
 * @param {string} filename - Name for the exported file
 */
export const exportProcessorsToExcel = (processors, filename = 'processors_export.xlsx') => {
  // Transform processor objects to Excel-friendly format
  const excelData = processors.map(processor => ({
    'ID': processor.id || '',
    'Business Name': processor.businessName || processor.nameOfBusiness || '',
    'Owner Name': processor.ownerName || processor.nameOfTheOwner || '',
    'Address': processor.address || '',
    'Contact Number': processor.contact || '',
    'Business Type': processor.businessType || '',
    'Status': processor.businessStatus || processor.statusOfBusiness || '',
    'Years in Operation': processor.yearsInOperation || processor.lengthOfBusinessOperationInYears || '',
    'Initial Investment': processor.initialInvestment || '',
    'Average Annual Income': processor.averageAnnualGrossIncome || '',
    'Employees': processor.numberOfEmployees || '',
    'Latitude': processor.latitude || '',
    'Longitude': processor.longitude || '',
    'Address Owner': processor.addressOwner || '',
    'Processing Capacity': processor.processingCapacity || processor.quantityOfCacaoBeansPerProcessingCycle || '',
    'Frequency of Processing': processor.frequencyOfProcessing || '',
    'Preferred Variety': processor.preferredVarietyOfCacao || '',
    'Variety Processed': processor.varietyOfCacaoProcessed || '',
    'Source of Cacao': processor.sourceOfCacao || '',
    'Market Reach': processor.marketReach || '',
    'Barangay': processor.barangay || '',
    'Municipality': processor.municipality || ''
  }));
  
  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Processors');
  
  // Save file
  XLSX.writeFile(workbook, filename);
};
